import React from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const DraftNotice = ({ t }) => (
  <div className="mb-8 rounded-lg border-l-4 border-amber-400 bg-amber-50 p-4 text-amber-900">
    <p className="font-bold">{t('legal.draftNotice.label')}</p>
    <p className="mt-1 text-sm leading-relaxed">{t('legal.draftNotice.body')}</p>
  </div>
);

const DataTable = ({ t }) => {
  const table = t('legal.privacy.sections.data.table', { returnObjects: true });
  const rows = Array.isArray(table?.rows) ? table.rows : [];

  return (
    <div className="not-prose my-6 overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 font-semibold text-gray-900">{table?.colCategory}</th>
            <th className="px-4 py-3 font-semibold text-gray-900">{table?.colExamples}</th>
            <th className="px-4 py-3 font-semibold text-gray-900">{table?.colPurpose}</th>
            <th className="px-4 py-3 font-semibold text-gray-900">{table?.colLegalBasis}</th>
            <th className="px-4 py-3 font-semibold text-gray-900">{table?.colRetention}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, index) => (
            <tr key={row.category || index} className="align-top">
              <td className="px-4 py-3 font-semibold text-gray-900">{row.category}</td>
              <td className="px-4 py-3 text-gray-600">{row.examples}</td>
              <td className="px-4 py-3 text-gray-600">{row.purpose}</td>
              <td className="px-4 py-3 text-gray-600">{row.legalBasis}</td>
              <td className="px-4 py-3 text-csl-green font-medium">{row.retention}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  const s = 'legal.privacy.sections';

  const useItems = t(`${s}.use.items`, { returnObjects: true });
  const sharingItems = t(`${s}.sharing.items`, { returnObjects: true });
  const rightsItems = t(`${s}.rights.items`, { returnObjects: true });
  const ccpaCategories = t(`${s}.ccpa.categories`, { returnObjects: true });
  const controllerEmail = t(`${s}.controller.emailValue`);

  return (
    <>
      <SEO
        path="/privacy-policy"
        title={t('legal.privacy.seo.title')}
        description={t('legal.privacy.seo.description')}
        keywords={t('legal.privacy.seo.keywords')}
      />
      <Header />
      <main className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-xl shadow-lg animate-fade-in animate-slide-up">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('legal.privacy.title')}</h1>
            <p className="text-gray-600 mb-4">{t('legal.privacy.lastUpdated')}</p>

            <DraftNotice t={t} />

            <div className="prose prose-green max-w-none">
              <h2>{t(`${s}.intro.heading`)}</h2>
              <p>{t(`${s}.intro.p1`)}</p>
              <p>{t(`${s}.intro.p2`)}</p>
              <p>{t(`${s}.intro.p3`)}</p>

              <h2>{t(`${s}.controller.heading`)}</h2>
              <p>{t(`${s}.controller.p1`)}</p>
              <p>
                <strong>{t(`${s}.controller.entityLabel`)}:</strong> {t(`${s}.controller.entityValue`)}<br />
                <strong>{t(`${s}.controller.addressLabel`)}:</strong> {t(`${s}.controller.addressValue`)}<br />
                <strong>{t(`${s}.controller.emailLabel`)}:</strong> {controllerEmail}<br />
                <strong>{t(`${s}.controller.dpoLabel`)}:</strong> {t(`${s}.controller.dpoValue`)}
              </p>
              <p className="text-sm italic text-amber-700">{t(`${s}.controller.dpoNote`)}</p>

              <h2>{t(`${s}.data.heading`)}</h2>
              <p>{t(`${s}.data.intro`)}</p>
              <DataTable t={t} />

              <h2>{t(`${s}.use.heading`)}</h2>
              <p>{t(`${s}.use.intro`)}</p>
              <ul>
                {(Array.isArray(useItems) ? useItems : []).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>

              <h2>{t(`${s}.cookies.heading`)}</h2>
              <p>
                {t(`${s}.cookies.p1`)}{' '}
                <a href="/cookies-policy">{t(`${s}.cookies.linkText`)}</a>.
              </p>

              <h2>{t(`${s}.sharing.heading`)}</h2>
              <p>{t(`${s}.sharing.intro`)}</p>
              <ul>
                {(Array.isArray(sharingItems) ? sharingItems : []).map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}:</strong> {item.description}
                  </li>
                ))}
              </ul>
              <p className="text-sm italic text-amber-700">{t(`${s}.sharing.subprocessorsNote`)}</p>

              <h2>{t(`${s}.transfers.heading`)}</h2>
              <p>{t(`${s}.transfers.p1`)}</p>
              <p>{t(`${s}.transfers.p2`)}</p>
              <p className="text-sm italic text-amber-700">{t(`${s}.transfers.placeholder`)}</p>

              <h2>{t(`${s}.retention.heading`)}</h2>
              <p>{t(`${s}.retention.p1`)}</p>
              <p className="text-sm italic text-amber-700">{t(`${s}.retention.placeholder`)}</p>

              <h2>{t(`${s}.security.heading`)}</h2>
              <p>{t(`${s}.security.p1`)}</p>

              <h2>{t(`${s}.rights.heading`)}</h2>
              <p>
                {t(`${s}.rights.intro`)}{' '}
                <a href={`mailto:${controllerEmail}`}>{controllerEmail}</a>.{' '}
                {t(`${s}.rights.introSuffix`)}
              </p>
              <ul>
                {(Array.isArray(rightsItems) ? rightsItems : []).map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}:</strong> {item.description}
                  </li>
                ))}
              </ul>
              <p className="text-sm italic text-amber-700">{t(`${s}.rights.complaintPlaceholder`)}</p>

              <h2>{t(`${s}.ccpa.heading`)}</h2>
              <p>{t(`${s}.ccpa.intro`)}</p>
              <h3>{t(`${s}.ccpa.categoriesTitle`)}</h3>
              <p>{t(`${s}.ccpa.categoriesIntro`)}</p>
              <ul>
                {(Array.isArray(ccpaCategories) ? ccpaCategories : []).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <h3>{t(`${s}.ccpa.sourcesTitle`)}</h3>
              <p>{t(`${s}.ccpa.sourcesBody`)}</p>
              <h3>{t(`${s}.ccpa.sellTitle`)}</h3>
              <p>{t(`${s}.ccpa.sellBody`)}</p>
              <p className="text-sm italic text-amber-700">{t(`${s}.ccpa.sellPlaceholder`)}</p>
              <h3>{t(`${s}.ccpa.nondiscTitle`)}</h3>
              <p>{t(`${s}.ccpa.nondiscBody`)}</p>
              <h3>{t(`${s}.ccpa.exerciseTitle`)}</h3>
              <p>
                {t(`${s}.ccpa.exerciseBody`)}{' '}
                <a href={`mailto:${controllerEmail}`}>{controllerEmail}</a>,{' '}
                {t(`${s}.ccpa.exerciseSuffix`)}
              </p>

              <h2>{t(`${s}.children.heading`)}</h2>
              <p>
                {t(`${s}.children.p1`)}{' '}
                <a href={`mailto:${controllerEmail}`}>{controllerEmail}</a>.
              </p>

              <h2>{t(`${s}.changes.heading`)}</h2>
              <p>{t(`${s}.changes.p1`)}</p>

              <h2>{t(`${s}.contact.heading`)}</h2>
              <p>{t(`${s}.contact.p1`)}</p>
              <p>
                <strong>{t(`${s}.contact.emailLabel`)}:</strong> {t(`${s}.contact.emailValue`)}<br />
                <strong>{t(`${s}.contact.addressLabel`)}:</strong> {t(`${s}.contact.addressValue`)}
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
